// BillingLifecycleStrip.js — the app-shell trial and past_due strip (D-10,
// LIFE-03, LIFE-04, 51-UI-SPEC.md Surface 3).
//
// A structural clone of AccountStatusBanner.js and MessagingOnboardingBanner.js:
// a bare <div> with no minimum-height utility, no fixed pixel height, and no
// inline CSS height property of any kind. Those two banners are already
// mounted in App.js's flex column (a definite-height ancestor with no size
// opinion of its own at this level) with no size opinion of their own, and
// <main>'s own flex-basis absorbs whatever space is left -- a third sibling
// in the identical shape is proven safe by that same working mechanism. This
// file must not introduce a fourth sizing rule into that visual family.
//
// D-10's visibility window (eleven quiet days, then unmissable): the trial's
// final 3 days, and the whole past_due window. Both the window and every
// string this strip renders come from src/utils/billingLifecycle.js (plan
// 51-07) -- this file owns none of that contract, only the layout and the
// Portal-call wiring, exactly like BillingTab.js does for Surface 1.
//
// The strip offers no way to make it go away on demand, unlike
// MessagingOnboardingBanner's own "not now" affordance -- undoing the one
// thing warning a trialing owner of an imminent charge would defeat the
// reason this component exists. It disappears on its own when the condition
// clears (the trial converts, or the card succeeds on retry).
//
// Ground-truth correction: the plan's own <interfaces> section describes an
// earlier lifecycleStripCopyFor signature returning { copy, variant,
// linkLabel }. A direct read of the shipped src/utils/billingLifecycle.js
// (confirmed against 51-07-SUMMARY.md's locked export signatures) shows it
// returns { body, actionLabel } and takes no `daysRemaining` input of its
// own -- it derives that internally via daysRemainingUntil. This component
// follows the actual shipped module, not the plan's stale interface note
// (Rule 1: the module it must consume, not re-derive, is the real one).
// `variant` (trial vs. past_due) is derived here from subscriptionStatus
// directly, since the real module carries no such field.

import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, CreditCard } from 'lucide-react';
import { useToast } from '../../../contexts/ToastContext';
import Spinner from '../../ui/primitives/Spinner';
import { usePendingAction } from '../../../hooks/usePendingAction';
import { createBillingPortalSession } from '../../../services/billingService';
import { useTrialChargeAmount } from '../../../hooks/useTrialChargeAmount';
import {
    daysRemainingUntil,
    shouldShowLifecycleStrip,
    lifecycleStripCopyFor,
} from '../../../utils/billingLifecycle';
import { SUPPORT_EMAIL } from '../../../config/support';

/**
 * Props (from App.js's app-shell mount, third sibling after AccountStatusBanner):
 *   workspaceId     -- for createBillingPortalSession
 *   billing         -- selectedWorkspace?.billing (the D-03 mirror), absent
 *                      on Free / admin-assigned / TAW-adjacent workspaces by
 *                      design (D-04) -- never a loading state, so an absent
 *                      billing map simply means "nothing to show", not "wait"
 *   isOwner         -- selectedWorkspace?.ownerId === user?.uid
 *                      (App.js's existing canManageWorkspaceAccess memo)
 *   workspaceStatus -- selectedWorkspace?.status. Accepted for prop-shape
 *                      symmetry with the two other app-shell banners this
 *                      strip sits beside, but not read by this component's
 *                      own visibility logic -- the mobile suspended-workspace
 *                      collapse rule (hidden sm:block below 640px) is applied
 *                      by the App.js mount site's own wrapper, not in here.
 */
export default function BillingLifecycleStrip({ workspaceId, billing, isOwner, workspaceStatus: _workspaceStatus }) {
    const subscriptionStatus = billing?.subscriptionStatus || null;
    const trialEnd = billing?.trialEnd ?? null;

    const daysRemaining = daysRemainingUntil(trialEnd, Date.now());
    const isVisible = shouldShowLifecycleStrip({ subscriptionStatus, daysRemaining });

    // Hooks cannot follow a conditional early return, so the `enabled` flag
    // carries the gating instead. useTrialChargeAmount issues no network
    // call at all when `enabled` is false -- which is exactly why it exists:
    // this component is mounted for every signed-in user on every route, and
    // a member's browser must never even resolve the dollar figure (D-09,
    // T-51-08-01), nor should an owner's browser resolve one for a state
    // (active, no mirror, or a past_due window that carries no amount at
    // all) that never needs it.
    const { amountLabel } = useTrialChargeAmount(workspaceId, {
        enabled: isVisible && isOwner && subscriptionStatus === 'trialing',
    });

    const { showToast } = useToast();
    const openPortal = async () => {
        // href="#" with preventDefault, not a bare onClick with no href --
        // the Portal URL is not known until the callable resolves, so this
        // cannot be a plain <a href={url}>. usePendingAction guards a fast
        // repeat click synchronously. The portal opens in a new tab, so the
        // page never unloads and the busy state clears when the call settles.
        try {
            const { url } = await createBillingPortalSession({ workspaceId });
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch (err) {
            // Same three causes plan 51-07's Billing tab distinguishes,
            // surfaced through the existing toast rather than mutating this
            // strip's own copy -- a strip that turns into an error message
            // stops telling the user the one thing it exists to tell them.
            if (err?.code === 'functions/permission-denied') {
                showToast('Only the workspace owner can manage payment details.', 'error');
            } else if (err?.code === 'functions/failed-precondition') {
                showToast(
                    "Either this workspace's plan is managed by its parent team, or there's no payment method on file yet.",
                    'error'
                );
            } else {
                showToast(`Something went wrong opening billing. Please try again, or contact ${SUPPORT_EMAIL}.`, 'error');
            }
        }
    };
    const { run: runPortal, pending: portalBusy } = usePendingAction(openPortal);
    const handleUpdatePayment = (event) => {
        event.preventDefault();
        return runPortal();
    };

    if (!isVisible) return null;

    const copy = lifecycleStripCopyFor({ subscriptionStatus, trialEnd, isOwner, amountLabel });
    // Defensive only -- shouldShowLifecycleStrip already agreed this state
    // should render, so lifecycleStripCopyFor returning null here would mean
    // the two functions disagree, which is a bug in the shared module, not a
    // state this component should paper over silently.
    if (!copy) return null;

    const variant = subscriptionStatus === 'past_due' ? 'past_due' : 'trial';
    const Icon = variant === 'past_due' ? CreditCard : Clock;

    return (
        <div
            role="status"
            className="border-b border-[color:var(--color-warning)]/20 bg-[color:var(--surface-warning)] px-4 py-2 text-sm text-[color:var(--text-primary)] lg:px-6"
        >
            <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-2">
                <Icon size={16} className="shrink-0" style={{ color: 'var(--color-warning)' }} aria-hidden="true" />
                <span>{copy.body}</span>
                {copy.actionLabel && variant === 'trial' && (
                    <Link to="/manage-workspace?tab=billing" className="font-medium underline">
                        {copy.actionLabel}
                    </Link>
                )}
                {copy.actionLabel && variant === 'past_due' && (
                    <a
                        href="#"
                        onClick={handleUpdatePayment}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-disabled={portalBusy}
                        aria-busy={portalBusy ? 'true' : undefined}
                        className="inline-flex items-center gap-1.5 font-medium underline"
                    >
                        {portalBusy && <Spinner size="xs" />}
                        {portalBusy ? 'Updating…' : copy.actionLabel}
                    </a>
                )}
            </div>
        </div>
    );
}
